import styled from 'styled-components';
import { Button, Progress, Spin, theme, Tooltip, Typography } from 'antd';
import {
  DeleteOutlined,
  LoadingOutlined,
  PaperClipOutlined,
  ReloadOutlined,
  DownloadOutlined,
} from '@ant-design/icons';

import {
  ChunkFileUploadMode,
  UploadFileStatus,
  IUploadingItem,
} from './ChunkFileUpload';
import { theme as customTheme } from '../styles/theme';

interface IProps {
  file: IUploadingItem;
  mode: ChunkFileUploadMode;
  progress?: number;
  deleteTooltip?: string;
  downloadTooltip?: string;
  retryTooltip?: string;
  onDelete?(file: IUploadingItem): void;
  onRetry?(file: IUploadingItem): void;
  onDownload?(file: IUploadingItem): void;
}

const DragUploadFileListItem: React.FC<IProps> = ({
  file,
  mode,
  onDelete,
  progress,
  onRetry,
  onDownload,
  deleteTooltip,
  downloadTooltip,
  retryTooltip,
}) => {
  return (
    <Wrapper>
      <FileInfoDetails error={file.status === UploadFileStatus.Error}>
        <Typography.Text>
          <PaperClipOutlined style={{ marginRight: customTheme.spacing.xs }} />
          {file.name}
        </Typography.Text>
        <FileActions>
          {file.status === UploadFileStatus.Uploading && (
            <Spin
              size="small"
              indicator={<LoadingOutlined />}
              style={{ marginRight: customTheme.spacing.xs }}
            />
          )}
          {file.status === UploadFileStatus.Error && onRetry && (
            <Tooltip title={retryTooltip}>
              <Button
                type="default"
                onClick={() => {
                  onRetry(file);
                }}
                size="small"
                icon={<ReloadOutlined />}
                style={{ marginRight: customTheme.spacing.xs }}
              />
            </Tooltip>
          )}
          {mode !== ChunkFileUploadMode.View && onDelete && (
            <Tooltip title={deleteTooltip}>
              <Button
                type="default"
                danger
                onClick={() => {
                  onDelete?.(file);
                }}
                size="small"
                style={{ marginRight: customTheme.spacing.xs }}
                icon={
                  <DeleteOutlined
                    style={{ color: `${customTheme.colors.error}` }}
                  />
                }
              />
            </Tooltip>
          )}
          {file?.id && onDownload && (
            <Tooltip title={downloadTooltip}>
              <Button
                type="default"
                onClick={() => onDownload?.(file)}
                size="small"
                icon={<DownloadOutlined />}
              />
            </Tooltip>
          )}
        </FileActions>
      </FileInfoDetails>
      {!!progress && <Progress percent={progress} size="small" />}
    </Wrapper>
  );
};

export default DragUploadFileListItem;

const Wrapper = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.sm};
`;

const FileInfoDetails = styled.div<{ error: boolean }>`
  position: relative;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${({ theme }) => theme.spacing.xs} 12px;
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  transition: background-color 0.3s ease-in-out;
  background-color: ${({ error, theme }) =>
    error ? theme.colors.error : theme.colors.gray[50]};
  &:last-child {
    margin-bottom: 0;
  }
`;

const FileActions = styled.div`
  display: flex;
  align-items: center;
  min-height: ${({ theme }) => theme.spacing.xl};
  .ant-typography {
    color: ${({ theme }) => theme.colors.primary};
  }
  .anticon-delete {
    font-size: 14px;
    color: ${({ theme }) => theme.colors.primary};
  }
`;
